import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, LogOut, FileText, CheckCircle, Crown, Users, Mic, Shield, Eye, Wifi, WifiOff } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TherapistDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [vipOrders, setVipOrders] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings');
  const [therapistOnline, setTherapistOnline] = useState(false);
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [liveLoggedIn, setLiveLoggedIn] = useState([]);

  useEffect(() => {
    fetchBookings();
    fetchVipOrders();
    fetchRecordings();
    fetchMemberships();
    fetchLiveTracking();
    fetchTherapistStatus();

    // Refresh live tracking every 15s
    const liveInterval = setInterval(fetchLiveTracking, 15000);
    // Therapist heartbeat every 30s
    const hbInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token) {
        axios.post(`${API}/tracking/login-heartbeat`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    }, 30000);

    return () => { clearInterval(liveInterval); clearInterval(hbInterval); };
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/bookings/all`, config);
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVipOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/vip-orders`, config);
      setVipOrders(response.data);
    } catch (error) {
      console.error('Failed to load VIP orders:', error);
    }
  };

  const fetchRecordings = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/voice-recordings`, config);
      setRecordings(response.data);
    } catch (error) {
      console.error('Failed to load recordings:', error);
    }
  };

  const fetchMemberships = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/memberships`, config);
      setMemberships(response.data);
    } catch (error) {
      console.error('Failed to load memberships:', error);
    }
  };

  const fetchLiveTracking = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/tracking/details`, config);
      setLiveVisitors(response.data.active_visitors || []);
      setLiveLoggedIn(response.data.logged_in_users || []);
    } catch (error) {
      console.error('Failed to load live tracking:', error);
    }
  };

  const fetchTherapistStatus = async () => {
    try {
      const response = await axios.get(`${API}/tracking/stats`);
      setTherapistOnline(response.data.therapist_online);
    } catch (error) {
      console.error('Failed to fetch therapist status:', error);
    }
  };

  const toggleTherapistOnline = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const newStatus = !therapistOnline;
      await axios.put(`${API}/tracking/therapist-status?online=${newStatus}`, {}, config);
      setTherapistOnline(newStatus);
      toast.success(newStatus ? 'You are now Online' : 'You are now Offline');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRecordingStatus = async (recordingId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/voice-recordings/${recordingId}?status=${newStatus}`, {}, config);
      toast.success('Recording status updated');
      fetchRecordings();
    } catch (error) {
      toast.error('Failed to update recording status');
    }
  };

  const handleMembershipPayment = async (membershipId, newPaymentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/memberships/${membershipId}?payment_status=${newPaymentStatus}`, {}, config);
      toast.success('Membership payment status updated');
      fetchMemberships();
    } catch (error) {
      toast.error('Failed to update membership');
    }
  };

  const handleVipStatusChange = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/vip-orders/${orderId}`, { status: newStatus }, config);
      toast.success('VIP order status updated');
      fetchVipOrders();
    } catch (error) {
      toast.error('Failed to update VIP order status');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(
        `${API}/bookings/${selectedBooking.id}/notes`,
        { booking_id: selectedBooking.id, notes },
        config
      );
      toast.success('Notes saved successfully');
      fetchBookings();
      setSelectedBooking(null);
      setNotes('');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/bookings/${bookingId}/status?status=${newStatus}`, {}, config);
      toast.success('Status updated successfully');
      if (newStatus === 'completed') {
        toast.success('Feedback request sent to client via WhatsApp!');
      }
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendFeedback = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/bookings/${bookingId}/send-feedback`, {}, config);
      toast.success('Feedback request sent to client via WhatsApp!');
    } catch (error) {
      toast.error('Failed to send feedback request');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      confirmed: 'bg-green-500/20 text-green-300 border-green-500/30',
      completed: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      cancelled: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[status] || colors.pending;
  };

  const getVipStatusColor = (status) => {
    const colors = {
      new: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      contacted: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      demo_scheduled: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      negotiating: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      converted: 'bg-green-500/20 text-green-300 border-green-500/30',
      declined: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[status] || colors.new;
  };

  const getClientTypeLabel = (type) => {
    const labels = {
      celebrity: '⭐ Celebrity',
      bureaucrat: '🏛️ Bureaucrat',
      high_profile: '💎 High Profile',
      other: '👤 Other'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900">
      {/* Header */}
      <header className="border-b border-purple-500/30 bg-purple-900/40 backdrop-blur-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8 text-purple-300" />
              <div>
                <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>Therapist Dashboard</h1>
                <p className="text-purple-300 text-sm">Welcome, {user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Online/Offline Toggle */}
              <button
                onClick={toggleTherapistOnline}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  therapistOnline
                    ? 'bg-green-500/20 border border-green-400/50 text-green-300 hover:bg-green-500/30'
                    : 'bg-gray-600/20 border border-gray-500/40 text-gray-400 hover:bg-gray-500/30'
                }`}
                data-testid="therapist-online-toggle"
              >
                {therapistOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                {therapistOnline ? 'Online' : 'Offline'}
                <div className={`w-2.5 h-2.5 rounded-full ${therapistOnline ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
              </button>

              {/* Live viewers count */}
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 text-sm">
                <Eye className="w-4 h-4" />
                <span><strong>{liveVisitors.length}</strong> viewing</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-400/30 text-purple-300 text-sm">
                <Users className="w-4 h-4" />
                <span><strong>{liveLoggedIn.length}</strong> logged in</span>
              </div>

              <Button
                data-testid="therapist-logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="text-purple-200 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-12">
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-4" data-testid="stat-total-bookings">
            <p className="text-purple-300 text-xs mb-1">Total Bookings</p>
            <p className="text-2xl font-bold text-white">{bookings.length}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-4" data-testid="stat-pending">
            <p className="text-purple-300 text-xs mb-1">Pending</p>
            <p className="text-2xl font-bold text-yellow-300">{bookings.filter(b => b.status === 'pending').length}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-4" data-testid="stat-confirmed">
            <p className="text-purple-300 text-xs mb-1">Confirmed</p>
            <p className="text-2xl font-bold text-green-300">{bookings.filter(b => b.status === 'confirmed').length}</p>
          </Card>
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-4" data-testid="stat-completed">
            <p className="text-purple-300 text-xs mb-1">Completed</p>
            <p className="text-2xl font-bold text-blue-300">{bookings.filter(b => b.status === 'completed').length}</p>
          </Card>
          <Card className="bg-amber-900/40 backdrop-blur-lg border border-amber-500/30 p-4" data-testid="stat-vip-orders">
            <p className="text-amber-300 text-xs mb-1">VIP Orders</p>
            <p className="text-2xl font-bold text-amber-300">{vipOrders.length}</p>
          </Card>
          <Card className="bg-red-900/40 backdrop-blur-lg border border-red-500/30 p-4" data-testid="stat-recordings">
            <p className="text-red-300 text-xs mb-1">Voice Msgs</p>
            <p className="text-2xl font-bold text-red-300">{recordings.length}</p>
          </Card>
          <Card className="bg-yellow-900/40 backdrop-blur-lg border border-yellow-500/30 p-4" data-testid="stat-memberships">
            <p className="text-yellow-300 text-xs mb-1">Members</p>
            <p className="text-2xl font-bold text-yellow-300">{memberships.length}</p>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all text-sm ${
              activeTab === 'bookings'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-900/40 text-purple-300 hover:bg-purple-800/40'
            }`}
            data-testid="tab-bookings"
          >
            <Users className="w-4 h-4" />
            <span>Bookings ({bookings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('vip-orders')}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all text-sm ${
              activeTab === 'vip-orders'
                ? 'bg-amber-600 text-white'
                : 'bg-amber-900/40 text-amber-300 hover:bg-amber-800/40'
            }`}
            data-testid="tab-vip-orders"
          >
            <Crown className="w-4 h-4" />
            <span>VIP Orders ({vipOrders.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('recordings')}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all text-sm ${
              activeTab === 'recordings'
                ? 'bg-red-600 text-white'
                : 'bg-red-900/40 text-red-300 hover:bg-red-800/40'
            }`}
            data-testid="tab-recordings"
          >
            <Mic className="w-4 h-4" />
            <span>Voice Recordings ({recordings.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('memberships')}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all text-sm ${
              activeTab === 'memberships'
                ? 'bg-yellow-600 text-white'
                : 'bg-yellow-900/40 text-yellow-300 hover:bg-yellow-800/40'
            }`}
            data-testid="tab-memberships"
          >
            <Shield className="w-4 h-4" />
            <span>Members ({memberships.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('live')}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-semibold transition-all text-sm ${
              activeTab === 'live'
                ? 'bg-cyan-600 text-white'
                : 'bg-cyan-900/40 text-cyan-300 hover:bg-cyan-800/40'
            }`}
            data-testid="tab-live"
          >
            <Eye className="w-4 h-4" />
            <span>Live ({liveVisitors.length + liveLoggedIn.length})</span>
          </button>
        </div>

        {/* Bookings Tab Content */}
        {activeTab === 'bookings' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>All Bookings</h2>
            
            {bookings.length === 0 ? (
              <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-12 text-center" data-testid="no-bookings">
                <p className="text-purple-200 text-lg">No bookings yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-6" data-testid={`therapist-booking-${booking.id}`}>
                    <div className="grid md:grid-cols-6 gap-6 items-center">
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Patient Name</p>
                        <p className="text-white font-semibold">{booking.name}</p>
                      </div>
                      <div>
                        <p className="text-purple-400 text-sm mb-1">Age / Location</p>
                        <p className="text-white">{booking.age} yrs / {booking.location}</p>
                      </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Contact</p>
                    <p className="text-white">{booking.phone}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Package</p>
                    <p className="text-white capitalize">{booking.session_type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-purple-400 text-sm mb-1">Status</p>
                    <Select value={booking.status} onValueChange={(value) => handleStatusChange(booking.id, value)}>
                      <SelectTrigger className={`w-full ${getStatusColor(booking.status)} border`} data-testid={`status-select-${booking.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          data-testid={`view-details-${booking.id}`}
                          onClick={() => {
                            setSelectedBooking(booking);
                            setNotes(booking.therapist_notes || '');
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-purple-900/95 backdrop-blur-xl border border-purple-500/30 text-white max-w-2xl" data-testid={`booking-details-dialog-${booking.id}`}>
                        <DialogHeader>
                          <DialogTitle className="text-2xl text-white">Booking Details</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-purple-400 text-sm">Patient Name</p>
                              <p className="text-white font-semibold">{booking.name}</p>
                            </div>
                            <div>
                              <p className="text-purple-400 text-sm">Age</p>
                              <p className="text-white">{booking.age} years</p>
                            </div>
                            <div>
                              <p className="text-purple-400 text-sm">Location</p>
                              <p className="text-white">{booking.location}</p>
                            </div>
                            <div>
                              <p className="text-purple-400 text-sm">Contact</p>
                              <p className="text-white">{booking.phone}</p>
                            </div>
                            <div>
                              <p className="text-purple-400 text-sm">Booking Date</p>
                              <p className="text-white">{format(new Date(booking.booking_date), 'MMM dd, yyyy')}</p>
                            </div>
                            <div>
                              <p className="text-purple-400 text-sm">Package</p>
                              <p className="text-white capitalize">{booking.session_type.replace('_', ' ')}</p>
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-purple-400 text-sm mb-2">Health Issues / Concerns</p>
                            <div className="bg-purple-950/50 border border-purple-500/30 rounded-lg p-4">
                              <p className="text-white">{booking.health_issues}</p>
                            </div>
                          </div>

                          <div>
                            <p className="text-purple-400 text-sm mb-2">Therapist Notes</p>
                            <Textarea
                              data-testid={`therapist-notes-${booking.id}`}
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              rows={4}
                              placeholder="Add your session notes here..."
                              className="bg-purple-950/50 border-purple-500/30 text-white resize-none"
                            />
                          </div>

                          <Button
                            data-testid={`save-notes-${booking.id}`}
                            onClick={handleSaveNotes}
                            disabled={savingNotes}
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {savingNotes ? 'Saving...' : 'Save Notes'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Send Feedback Button - Only show for completed sessions */}
                    {booking.status === 'completed' && (
                      <Button
                        data-testid={`send-feedback-${booking.id}`}
                        onClick={() => handleSendFeedback(booking.id)}
                        className="bg-green-600 hover:bg-green-700 text-white w-full"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        Request Feedback
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
          </>
        )}

        {/* VIP Orders Tab Content */}
        {activeTab === 'vip-orders' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              <Crown className="inline w-8 h-8 text-amber-400 mr-2" />
              VIP & Premium Client Orders
            </h2>
            
            {vipOrders.length === 0 ? (
              <Card className="bg-amber-900/40 backdrop-blur-lg border border-amber-500/30 p-12 text-center" data-testid="no-vip-orders">
                <Crown className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                <p className="text-amber-200 text-lg">No VIP orders yet</p>
                <p className="text-amber-300/70 text-sm mt-2">Orders from celebrities, bureaucrats, and high-profile clients will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {vipOrders.map((order) => (
                  <Card key={order.id} className="bg-amber-900/30 backdrop-blur-lg border border-amber-500/30 p-6" data-testid={`vip-order-${order.id}`}>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Client Info */}
                      <div className="lg:col-span-2">
                        <div className="flex items-center space-x-3 mb-3">
                          <span className="text-2xl">{getClientTypeLabel(order.client_type).split(' ')[0]}</span>
                          <div>
                            <p className="text-white font-bold text-lg">{order.name}</p>
                            <p className="text-amber-300 text-sm">{getClientTypeLabel(order.client_type).split(' ').slice(1).join(' ')}</p>
                          </div>
                        </div>
                        {order.designation && (
                          <p className="text-amber-200 text-sm mb-1">
                            <span className="text-amber-400">Designation:</span> {order.designation}
                          </p>
                        )}
                        {order.organization && (
                          <p className="text-amber-200 text-sm">
                            <span className="text-amber-400">Organization:</span> {order.organization}
                          </p>
                        )}
                      </div>

                      {/* Contact Info */}
                      <div>
                        <p className="text-amber-400 text-sm mb-2">Contact Details</p>
                        <p className="text-white text-sm mb-1">📧 {order.email}</p>
                        <p className="text-white text-sm mb-1">📱 {order.phone}</p>
                        {order.whatsapp && order.whatsapp !== order.phone && (
                          <p className="text-white text-sm">💬 {order.whatsapp}</p>
                        )}
                        <p className="text-amber-200 text-sm mt-2">📍 {order.city}, {order.country}</p>
                      </div>

                      {/* Interest & Status */}
                      <div>
                        <p className="text-amber-400 text-sm mb-2">Device Interest</p>
                        <p className="text-white text-sm mb-3 capitalize">{order.device_interest?.replace(/_/g, ' ')}</p>
                        
                        <p className="text-amber-400 text-sm mb-2">Status</p>
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleVipStatusChange(order.id, value)}
                        >
                          <SelectTrigger className={`w-full ${getVipStatusColor(order.status)} border`} data-testid={`vip-status-select-${order.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="demo_scheduled">Demo Scheduled</SelectItem>
                            <SelectItem value="negotiating">Negotiating</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Requirements & Additional Info */}
                    {(order.requirements || order.preferred_contact_time || order.how_heard_about_us) && (
                      <div className="mt-4 pt-4 border-t border-amber-500/20">
                        <div className="grid md:grid-cols-3 gap-4">
                          {order.requirements && (
                            <div>
                              <p className="text-amber-400 text-sm mb-1">Requirements</p>
                              <p className="text-amber-100 text-sm">{order.requirements}</p>
                            </div>
                          )}
                          {order.preferred_contact_time && (
                            <div>
                              <p className="text-amber-400 text-sm mb-1">Preferred Contact Time</p>
                              <p className="text-amber-100 text-sm">{order.preferred_contact_time}</p>
                            </div>
                          )}
                          {order.how_heard_about_us && (
                            <div>
                              <p className="text-amber-400 text-sm mb-1">How They Found Us</p>
                              <p className="text-amber-100 text-sm">{order.how_heard_about_us}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 pt-4 border-t border-amber-500/20 flex flex-wrap gap-3">
                      <a
                        href={`https://wa.me/${order.whatsapp || order.phone}?text=Hi%20${order.name},%20this%20is%20from%20Divine%20Quanta%20regarding%20your%20inquiry%20about%20Quantum%20Science%20Therapy.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center"
                        data-testid={`vip-whatsapp-${order.id}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        WhatsApp
                      </a>
                      <a
                        href={`mailto:${order.email}?subject=Divine%20Quanta%20-%20Quantum%20Science%20Therapy%20Inquiry&body=Dear%20${order.name},%0A%0AThank%20you%20for%20your%20interest%20in%20Quantum%20Science%20Therapy.`}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center"
                        data-testid={`vip-email-${order.id}`}
                      >
                        📧 Email
                      </a>
                      <a
                        href={`tel:${order.phone}`}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center"
                        data-testid={`vip-call-${order.id}`}
                      >
                        📞 Call
                      </a>
                      <span className="text-amber-400/70 text-sm ml-auto">
                        {order.created_at && format(new Date(order.created_at), 'dd MMM yyyy, hh:mm a')}
                      </span>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Voice Recordings Tab */}
        {activeTab === 'recordings' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>Client Voice Recordings</h2>
            {recordings.length === 0 ? (
              <Card className="bg-red-900/30 backdrop-blur-lg border border-red-500/30 p-12 text-center" data-testid="no-recordings">
                <Mic className="w-12 h-12 text-red-400 mx-auto mb-4 opacity-50" />
                <p className="text-red-200 text-lg">No voice recordings yet</p>
                <p className="text-red-300/70 text-sm mt-2">Client voice messages will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {recordings.map((rec) => (
                  <Card key={rec.id} className="bg-red-900/20 backdrop-blur-lg border border-red-500/20 p-6" data-testid={`recording-${rec.id}`}>
                    <div className="grid md:grid-cols-4 gap-4 items-center">
                      <div>
                        <p className="text-red-400 text-sm mb-1">Client</p>
                        <p className="text-white font-bold">{rec.name}</p>
                        <p className="text-red-200 text-sm">{rec.phone}</p>
                        {rec.email && <p className="text-red-200 text-sm">{rec.email}</p>}
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-red-400 text-sm mb-2">Voice Message</p>
                        <audio controls className="w-full" data-testid={`audio-${rec.id}`}>
                          <source src={`${API}/voice-recordings/${rec.id}/audio?auth=${localStorage.getItem('token')}`} />
                        </audio>
                        <p className="text-red-300/50 text-xs mt-1">Duration: ~{rec.duration_seconds}s</p>
                      </div>
                      <div>
                        <p className="text-red-400 text-sm mb-2">Status</p>
                        <Select
                          value={rec.status}
                          onValueChange={(value) => handleRecordingStatus(rec.id, value)}
                        >
                          <SelectTrigger className={`w-full ${rec.status === 'new' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : rec.status === 'reviewed' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-green-500/20 text-green-300 border-green-500/30'} border`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-red-400/50 text-xs mt-2">
                          {rec.created_at && format(new Date(rec.created_at), 'dd MMM yyyy')}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Memberships Tab */}
        {activeTab === 'memberships' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>Memberships</h2>
            {memberships.length === 0 ? (
              <Card className="bg-yellow-900/30 backdrop-blur-lg border border-yellow-500/30 p-12 text-center" data-testid="no-memberships">
                <Shield className="w-12 h-12 text-yellow-400 mx-auto mb-4 opacity-50" />
                <p className="text-yellow-200 text-lg">No memberships yet</p>
                <p className="text-yellow-300/70 text-sm mt-2">Member registrations will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {memberships.map((mem) => (
                  <Card key={mem.id} className="bg-yellow-900/20 backdrop-blur-lg border border-yellow-500/20 p-6" data-testid={`membership-${mem.id}`}>
                    <div className="grid md:grid-cols-5 gap-4 items-center">
                      <div>
                        <p className="text-yellow-400 text-sm mb-1">Member</p>
                        <p className="text-white font-bold">{mem.name}</p>
                        <p className="text-yellow-200 text-sm">{mem.email}</p>
                        <p className="text-yellow-200 text-sm">{mem.phone}</p>
                      </div>
                      <div>
                        <p className="text-yellow-400 text-sm mb-1">Tier</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold capitalize ${
                          mem.tier === 'platinum' ? 'bg-amber-500/20 text-amber-300 border border-amber-400/40' :
                          mem.tier === 'gold' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-400/40' :
                          'bg-gray-500/20 text-gray-300 border border-gray-400/40'
                        }`}>
                          {mem.tier}
                        </span>
                      </div>
                      <div>
                        <p className="text-yellow-400 text-sm mb-1">Past Sessions</p>
                        <p className="text-white">{mem.previous_sessions}</p>
                        {mem.health_concern && (
                          <p className="text-yellow-200/70 text-xs mt-1 truncate" title={mem.health_concern}>{mem.health_concern}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-yellow-400 text-sm mb-2">Payment (Rs 500)</p>
                        <Select
                          value={mem.payment_status}
                          onValueChange={(value) => handleMembershipPayment(mem.id, value)}
                        >
                          <SelectTrigger className={`w-full ${mem.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : 'bg-green-500/20 text-green-300 border-green-500/30'} border`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-yellow-400 text-sm mb-1">Joined</p>
                        <p className="text-yellow-200 text-sm">
                          {mem.created_at && format(new Date(mem.created_at), 'dd MMM yyyy')}
                        </p>
                        <a
                          href={`https://wa.me/${mem.whatsapp || mem.phone}?text=Hi%20${mem.name},%20thank%20you%20for%20your%20Divine%20Quanta%20membership!`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs font-medium mt-2"
                          data-testid={`member-whatsapp-${mem.id}`}
                        >
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Live Tracking Tab */}
        {activeTab === 'live' && (
          <>
            <h2 className="text-3xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>Live Website Activity</h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Active Visitors */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-5 h-5 text-cyan-300" />
                  <h3 className="text-xl font-bold text-cyan-300">Active Visitors ({liveVisitors.length})</h3>
                  <span className="text-cyan-400/60 text-xs">(refreshes every 15s)</span>
                </div>
                {liveVisitors.length === 0 ? (
                  <Card className="bg-cyan-900/20 backdrop-blur-lg border border-cyan-500/20 p-8 text-center" data-testid="no-visitors">
                    <Eye className="w-10 h-10 text-cyan-400/40 mx-auto mb-3" />
                    <p className="text-cyan-200">No active visitors right now</p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {liveVisitors.map((v, i) => (
                      <Card key={v.visitor_id} className="bg-cyan-900/20 backdrop-blur-lg border border-cyan-500/20 p-4" data-testid={`visitor-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full animate-pulse"></div>
                            <div>
                              <p className="text-white font-medium text-sm">
                                {v.name || 'Anonymous Visitor'}
                              </p>
                              <p className="text-cyan-300/70 text-xs">
                                {v.email && <span className="mr-3">{v.email}</span>}
                                {v.phone && <span>{v.phone}</span>}
                                {!v.email && !v.phone && <span>No contact details</span>}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-cyan-400 text-xs">Page: /{v.page}</p>
                            <p className="text-cyan-300/50 text-xs">{v.seconds_ago}s ago</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Logged In Users */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-purple-300" />
                  <h3 className="text-xl font-bold text-purple-300">Logged In Users ({liveLoggedIn.length})</h3>
                </div>
                {liveLoggedIn.length === 0 ? (
                  <Card className="bg-purple-900/20 backdrop-blur-lg border border-purple-500/20 p-8 text-center" data-testid="no-logged-in">
                    <Users className="w-10 h-10 text-purple-400/40 mx-auto mb-3" />
                    <p className="text-purple-200">No logged-in users right now</p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {liveLoggedIn.map((u, i) => (
                      <Card key={u.user_id} className="bg-purple-900/20 backdrop-blur-lg border border-purple-500/20 p-4" data-testid={`logged-in-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
                            <div>
                              <p className="text-white font-medium text-sm">{u.name}</p>
                              <p className="text-purple-300/70 text-xs">
                                {u.email && <span className="mr-3">{u.email}</span>}
                                {u.phone && <span>{u.phone}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-purple-300/50 text-xs">{u.seconds_ago}s ago</p>
                            {u.phone && (
                              <a
                                href={`https://wa.me/91${u.phone}?text=Hi%20${u.name},%20this%20is%20from%20Divine%20Quanta.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs"
                              >
                                WhatsApp
                              </a>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TherapistDashboard;
